import { useCreateTopicMutation, useGetTopicsQuery } from "@api/quiz";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useToastEffect } from "@hooks/hooks";
import { CreateTopicDTO, TopicDTO } from "@onetutor/shared/dto";
import Fuse from "fuse.js";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TopicCard } from "./TopicCard";

interface TopicSelectorProps {
  courseId: string;
  selectedTopicId: string | undefined;
  select: (topic: TopicDTO | null) => void;
  creationEnabled?: boolean;
}

export function TopicSelector({
  courseId,
  selectedTopicId,
  select,
  creationEnabled = false,
}: TopicSelectorProps) {
  const { t } = useTranslation("quizManagementPage");

  const [searchInput, setSearchInput] = useState("");
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const {
    data: topicList = { topics: [] },
    error: topicsError,
    isError: isTopicsError,
  } = useGetTopicsQuery({ courseId, queryArgs: { includeUnpublished: true } });

  const [createTopic] = useCreateTopicMutation();

  useToastEffect({ error: topicsError, isError: isTopicsError });

  function isDuplicate(name: string): boolean {
    return topicList.topics.some(
      (topic) => topic.name.toLowerCase() === name.trim().toLowerCase(),
    );
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.currentTarget.setCustomValidity("");
    if (isDuplicate(e.target.value)) {
      e.currentTarget.setCustomValidity(t("duplicateTopicName"));
    }
    setNewName(e.target.value);
  };

  const handleCreateTopic = async () => {
    const name = newName.trim();
    if (!name) {
      return;
    }
    setCreating(true);
    try {
      const dto: CreateTopicDTO = { name };
      const res = await createTopic({ courseId, createTopicData: dto });
      if (res.data) {
        select(res.data);
        setNewName("");
      }
    } finally {
      setCreating(false);
    }
  };

  const fuse = useMemo(
    () =>
      new Fuse(topicList.topics, {
        threshold: 0.4,
        keys: ["name"],
      }),
    [topicList],
  );

  const filteredTopics = useMemo(() => {
    if (searchInput === "") {
      return topicList.topics;
    }
    return fuse.search(searchInput).map((result) => result.item);
  }, [searchInput, topicList, fuse]);

  return (
    <div className="my-4 flex flex-col">
      <label htmlFor="topic" className="self-start">
        <span className="font-semibold text-lg">{t("topic")}</span>
      </label>
      <div className="mt-sm mb-sm flex w-full max-w-3xl gap-2">
        <label className="input input-sm sm:input-md input-bordered flex flex-1 items-center rounded-xl">
          <input
            type="text"
            className="grow"
            placeholder={t("placeholderSearch")}
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
            }}
          />
          <MagnifyingGlassIcon className="h-6 w-6 sm:h-7 sm:w-7" />
        </label>
        {creationEnabled && (
          <form
            className="flex flex-1 gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              handleCreateTopic();
            }}
          >
            <input
              type="text"
              placeholder={t("newTopicNamePlaceholder")}
              className="input input-sm sm:input-md input-bordered flex-1"
              value={newName}
              onChange={handleInputChange}
              maxLength={50}
              minLength={1}
            />
            <button
              type="submit"
              className="btn btn-sm sm:btn-md btn-primary"
              disabled={!newName.trim() || creating}
            >
              {creating ? (
                <span className="loading loading-spinner loading-xs" />
              ) : (
                t("createTopic")
              )}
            </button>
          </form>
        )}
      </div>
      <div className="h-60 max-h-60 w-full max-w-3xl overflow-y-auto rounded-lg border border-base-300 p-1">
        <div className="grid grid-cols-1 gap-2">
          {filteredTopics.map((topic) => (
            <TopicCard
              key={topic.id}
              topic={topic}
              isSelected={topic.id === selectedTopicId}
              select={select}
              isDuplicate={isDuplicate}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
